/**
 * SessionDetailDrawer Component
 * 80%-width slide-out panel replacing SessionDetailModal.
 * Tabs: Pre Session (RSVPs + Resources), Post Session (Attendance + Coach Feedback + Participant Feedback), Internal Notes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Tabs,
  Badge,
  ScrollArea,
  Button,
  Group,
} from '@mantine/core';
import { Calendar, Clipboard, FileText, Edit3 } from 'react-feather';
import { sessionsApi } from '../../../../services/api/sessions';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import type { SessionDetailStats, Session, ResourceCategory } from '../../../../types';
import { SessionDetailHeader } from './SessionDetailHeader';
import { PreSessionTab } from './PreSessionTab';
import { PostSessionTab } from './PostSessionTab';
import { InternalNotesSection } from './InternalNotesSection';
import styles from './SessionDetailDrawer.module.css';

interface SessionDetailDrawerProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  sessionId: string;
  sessionNumber: number | null;
  topicName: string | null;
  groupName: string;
  groupId?: string;
  scheduledStart: string | null;
  timezone: string | null;
  displayStatus: { label: string; color: string };
  coachName: string | null;
  attendanceMarked?: boolean;
  feedbackSubmitted?: boolean;
  sessions?: Session[];
  categories?: ResourceCategory[];
  onComplete?: () => void;
  defaultTab?: string;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
  // MED-1076 / AC1: per-modality "Live Session Delivery" setting for this
  // cohort's group-coaching modality (Modality.live_session_delivery_enabled).
  // The "Edit Session Content" entry point is gated on this AND the global
  // live_session_delivery flag (fail-closed when undefined/false).
  liveSessionDeliveryModalityEnabled?: boolean;
}

export function SessionDetailDrawer({
  opened,
  onClose,
  cohortId,
  sessionId,
  sessionNumber,
  topicName,
  groupName,
  groupId,
  scheduledStart,
  timezone,
  displayStatus,
  coachName,
  attendanceMarked,
  feedbackSubmitted,
  sessions = [],
  categories = [],
  onComplete,
  defaultTab,
  onOpenFollowUps,
  liveSessionDeliveryModalityEnabled = false,
}: SessionDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<string | null>('pre_session');
  const [notesCount, setNotesCount] = useState(0);
  const [stats, setStats] = useState<SessionDetailStats | null>(null);
  const navigate = useNavigate();
  // MED-890 / AC1: global release-gate flag — fail-closed while loading.
  const liveSessionDeliveryFlag = useFeatureFlag('live_session_delivery');
  // MED-1076 / AC1: Live Session Delivery is governed by TWO switches and must
  // respect both — the global flag (above) AND the cohort's group-coaching
  // per-modality "Live Session Delivery" setting (prop). Fail-closed unless both
  // are on. Mirrors ModalityConfigPills' gate. Drives the "Edit Session Content"
  // entry point and (MED-1078 / E1.22) the read-only-with-override reframing of
  // the CM attendance section.
  const liveSessionDeliveryEnabled =
    liveSessionDeliveryFlag && liveSessionDeliveryModalityEnabled;
  const showEditSessionContent = liveSessionDeliveryEnabled;

  // Fetch session detail stats
  const fetchStats = useCallback(() => {
    sessionsApi
      .getSessionDetailStats(cohortId, sessionId)
      .then(setStats)
      .catch(() => {});
  }, [cohortId, sessionId]);

  // Reset tab on open and fetch stats
  useEffect(() => {
    if (opened) {
      setActiveTab(defaultTab || 'pre_session');
      setStats(null);
      setNotesCount(0);
      fetchStats();
    }
  }, [opened, cohortId, sessionId, fetchStats, defaultTab]);

  // Compute header stats strings
  const headerStats = stats
    ? {
        sessions: `${sessionNumber ?? '—'}/${stats.total_sessions_in_group}`,
        participants: `${stats.participant_count}`,
        // MED-1266: canonical rate + marking coverage rendered via AttendanceRate.
        attendanceCoverage: {
          rate: stats.attendance_percentage,
          marked: stats.attendance_marked,
          population: stats.attendance_population,
        },
        engagement: stats.avg_engagement !== null ? stats.avg_engagement.toFixed(1) : '—',
      }
    : null;

  const handleNotesChanged = useCallback((count: number) => {
    setNotesCount(count);
  }, []);

  const handleComplete = useCallback(() => {
    fetchStats();
    onComplete?.();
  }, [fetchStats, onComplete]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="80%"
      padding={0}
      withCloseButton={false}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      <SessionDetailHeader
        sessionNumber={sessionNumber}
        topicName={topicName}
        groupName={groupName}
        scheduledStart={scheduledStart}
        timezone={timezone}
        displayStatus={displayStatus}
        stats={headerStats}
        coachName={coachName}
        onClose={onClose}
      />

      {/* MED-784 / E1.3 AC13: Entry point to the Session Content Editor. */}
      {/* MED-890: hidden while the global live_session_delivery flag is off. */}
      {/* MED-1076: also hidden unless this cohort's group-coaching modality has */}
      {/* Live Session Delivery enabled (per-modality setting) — fail closed. */}
      {showEditSessionContent && (
        <Group
          justify="flex-end"
          px="md"
          py="xs"
          style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Button
            size="sm"
            variant="default"
            leftSection={<Edit3 size={14} />}
            onClick={() => {
              onClose();
              navigate(`/cohorts/${cohortId}/sessions/${sessionId}/content`);
            }}
          >
            {/* MED-1086 (E1.3m / AC12): one label everywhere — relabeled from
                "Edit Session Content". Behavior + gate unchanged (AC9). */}
            Build Content
          </Button>
        </Group>
      )}

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        className={styles.tabsContainer}
      >
        <Tabs.List className={styles.tabsList}>
          <Tabs.Tab
            value="pre_session"
            leftSection={<Calendar size={14} />}
          >
            Pre Session
          </Tabs.Tab>
          <Tabs.Tab
            value="post_session"
            leftSection={<Clipboard size={14} />}
          >
            Post Session
          </Tabs.Tab>
          <Tabs.Tab
            value="notes"
            leftSection={<FileText size={14} />}
            rightSection={
              notesCount > 0 ? (
                <Badge size="xs" variant="filled" color="gray" circle>
                  {notesCount}
                </Badge>
              ) : undefined
            }
          >
            Internal Notes
          </Tabs.Tab>
        </Tabs.List>

        <ScrollArea className={styles.tabContent}>
          <div style={{ padding: 'var(--mantine-spacing-md)' }}>
            <Tabs.Panel value="pre_session">
              <PreSessionTab
                cohortId={cohortId}
                sessionId={sessionId}
                sessions={sessions}
                categories={categories}
              />
            </Tabs.Panel>

            <Tabs.Panel value="post_session">
              <PostSessionTab
                cohortId={cohortId}
                sessionId={sessionId}
                groupId={groupId}
                attendanceMarked={attendanceMarked}
                feedbackSubmitted={feedbackSubmitted}
                onComplete={handleComplete}
                onOpenFollowUps={onOpenFollowUps}
                liveSessionDeliveryEnabled={liveSessionDeliveryEnabled}
              />
            </Tabs.Panel>

            <Tabs.Panel value="notes">
              <InternalNotesSection
                cohortId={cohortId}
                sessionId={sessionId}
                onNotesChanged={handleNotesChanged}
              />
            </Tabs.Panel>
          </div>
        </ScrollArea>
      </Tabs>
    </Drawer>
  );
}

export default SessionDetailDrawer;
